import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  CalendarClock,
  KanbanSquare,
  LayoutDashboard,
  LogOut,
  MessageSquare,
  Moon,
  RefreshCw,
  ShieldCheck,
  Sun,
  Table2,
  TrendingUp
} from "lucide-react";
import { Dashboard } from "./Dashboard";
import { Pipeline } from "./Pipeline";
import { LeadsTable } from "./LeadsTable";
import { LeadDrawer } from "./LeadDrawer";
import { TasksView } from "./TasksView";
import { ChatsView } from "./ChatsView";
import { AnalyticsView } from "./AnalyticsView";
import { Avatar } from "./widgets";
import { taskBucket } from "./format";
import {
  AuthError,
  createTask as apiCreateTask,
  deleteLead as apiDeleteLead,
  deleteTask as apiDeleteTask,
  listLeads,
  listTasks,
  logout,
  patchLead,
  updateTask as apiUpdateTask,
  type TaskInput
} from "./api";
import type { AdminLead, CrmView, LeadPriority, LeadStatus, LeadTask } from "./types";

const THEME_KEY = "ochro-theme-choice";

const NAV: { view: CrmView; label: string; icon: ReactNode }[] = [
  { view: "dashboard", label: "Pulpit", icon: <LayoutDashboard size={18} /> },
  { view: "pipeline", label: "Lejek", icon: <KanbanSquare size={18} /> },
  { view: "leads", label: "Leady", icon: <Table2 size={18} /> },
  { view: "tasks", label: "Zadania", icon: <CalendarClock size={18} /> },
  { view: "analytics", label: "Analityka", icon: <TrendingUp size={18} /> },
  { view: "chats", label: "Rozmowy", icon: <MessageSquare size={18} /> }
];

const VIEW_META: Record<CrmView, { title: string; subtitle: string }> = {
  dashboard: { title: "Pulpit", subtitle: "Przegląd sprzedaży i nowych zgłoszeń" },
  pipeline: { title: "Lejek sprzedaży", subtitle: "Etapy zgłoszeń od kontaktu do polisy" },
  leads: { title: "Leady", subtitle: "Wszystkie zgłoszenia z formularzy" },
  tasks: { title: "Zadania", subtitle: "Terminy kontaktu i działania do wykonania" },
  analytics: { title: "Analityka", subtitle: "Zachowanie użytkowników po zgodzie analitycznej" },
  chats: { title: "Rozmowy z czatbotem", subtitle: "Pytania zadane Wirtualnemu Asystentowi na stronie" }
};

const VIEW_NAMES = new Set<CrmView>(NAV.map((item) => item.view));
const ANALYTICS_DAYS = new Set([1, 7, 30, 90]);

function currentTheme(): "light" | "dark" {
  return document.documentElement.getAttribute("data-theme") === "dark" ? "dark" : "light";
}

function getAdminStateFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const viewParam = params.get("view");
  const daysParam = Number(params.get("days"));

  return {
    view: viewParam && VIEW_NAMES.has(viewParam as CrmView) ? (viewParam as CrmView) : "dashboard",
    analyticsDays: ANALYTICS_DAYS.has(daysParam) ? daysParam : 30
  };
}

function writeAdminStateToUrl(view: CrmView, analyticsDays: number) {
  const url = new URL(window.location.href);
  url.searchParams.set("view", view);

  if (view === "analytics") {
    url.searchParams.set("days", String(analyticsDays));
  } else {
    url.searchParams.delete("days");
  }

  window.history.replaceState(null, "", url);
}

export function CrmShell({ user, onLogout }: { user: string; onLogout: () => void }) {
  const [leads, setLeads] = useState<AdminLead[]>([]);
  const [tasks, setTasks] = useState<LeadTask[]>([]);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">("loading");
  const [errorMsg, setErrorMsg] = useState("");
  const [view, setView] = useState<CrmView>(() => getAdminStateFromUrl().view);
  const [analyticsDays, setAnalyticsDays] = useState(() => getAdminStateFromUrl().analyticsDays);
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [theme, setTheme] = useState<"light" | "dark">(currentTheme);

  const load = async () => {
    setLoadState("loading");
    setErrorMsg("");
    try {
      const [leadData, taskData] = await Promise.all([listLeads(), listTasks()]);
      setLeads(leadData);
      setTasks(taskData);
      setLoadState("ready");
    } catch (err) {
      if (err instanceof AuthError) {
        onLogout();
        return;
      }
      setErrorMsg("Nie udało się pobrać danych. Sprawdź połączenie i spróbuj ponownie.");
      setLoadState("error");
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    writeAdminStateToUrl(view, analyticsDays);
  }, [view, analyticsDays]);

  useEffect(() => {
    const syncFromUrl = () => {
      const next = getAdminStateFromUrl();
      setView(next.view);
      setAnalyticsDays(next.analyticsDays);
    };

    window.addEventListener("popstate", syncFromUrl);
    return () => window.removeEventListener("popstate", syncFromUrl);
  }, []);

  // Wyczyść zaznaczenie, jeśli lead zniknął z listy.
  useEffect(() => {
    if (selectedId && !leads.some((lead) => lead.id === selectedId)) setSelectedId(null);
  }, [leads, selectedId]);

  const toggleTheme = () => {
    const next = theme === "dark" ? "light" : "dark";
    document.documentElement.setAttribute("data-theme", next);
    try {
      localStorage.setItem(THEME_KEY, next);
    } catch {
      /* tryb prywatny — pomiń zapis */
    }
    setTheme(next);
  };

  const updateLead = async (
    id: string,
    patch: { status?: LeadStatus; owner?: string; priority?: LeadPriority }
  ) => {
    try {
      const updated = await patchLead(id, patch);
      setLeads((prev) => prev.map((lead) => (lead.id === id ? updated : lead)));
    } catch (err) {
      if (err instanceof AuthError) onLogout();
    }
  };

  const removeLead = async (id: string) => {
    try {
      await apiDeleteLead(id);
      setLeads((prev) => prev.filter((lead) => lead.id !== id));
      setTasks((prev) => prev.filter((task) => task.leadId !== id));
      setSelectedId(null);
    } catch (err) {
      if (err instanceof AuthError) onLogout();
    }
  };

  const createTask = async (input: TaskInput) => {
    try {
      const task = await apiCreateTask(input);
      setTasks((prev) => [...prev, task]);
    } catch (err) {
      if (err instanceof AuthError) onLogout();
    }
  };

  const replaceTask = (updated: LeadTask) =>
    setTasks((prev) => prev.map((task) => (task.id === updated.id ? updated : task)));

  const toggleTask = async (task: LeadTask, done: boolean) => {
    try {
      replaceTask(await apiUpdateTask(task.id, { done }));
    } catch (err) {
      if (err instanceof AuthError) onLogout();
    }
  };

  const rescheduleTask = async (task: LeadTask) => {
    const dueAt = new Date(new Date(task.dueAt).getTime() + 86_400_000).toISOString();
    try {
      replaceTask(await apiUpdateTask(task.id, { dueAt }));
    } catch (err) {
      if (err instanceof AuthError) onLogout();
    }
  };

  const removeTask = async (task: LeadTask) => {
    try {
      await apiDeleteTask(task.id);
      setTasks((prev) => prev.filter((t) => t.id !== task.id));
    } catch (err) {
      if (err instanceof AuthError) onLogout();
    }
  };

  const attentionCount = useMemo(
    () =>
      tasks.filter((task) => {
        const bucket = taskBucket(task.dueAt, task.done);
        return bucket === "overdue" || bucket === "today";
      }).length,
    [tasks]
  );

  const handleLogout = () => {
    logout();
    onLogout();
  };

  const selected = selectedId ? leads.find((lead) => lead.id === selectedId) ?? null : null;
  const meta = VIEW_META[view];

  return (
    <div className="crm-shell">
      <aside className="crm-sidebar">
        <div className="crm-brand">
          <span className="crm-brand-mark">
            <ShieldCheck size={20} aria-hidden="true" />
          </span>
          <div>
            <strong>Ochro</strong>
            <span className="crm-brand-tag">CRM</span>
          </div>
        </div>

        <nav className="crm-nav">
          {NAV.map((item) => (
            <button
              key={item.view}
              type="button"
              className={`crm-nav-item${view === item.view ? " is-active" : ""}`}
              onClick={() => setView(item.view)}
            >
              {item.icon}
              <span>{item.label}</span>
              {item.view === "tasks" && attentionCount > 0 && (
                <span className="crm-nav-badge" title={`${attentionCount} zadań wymaga uwagi`}>
                  {attentionCount}
                </span>
              )}
            </button>
          ))}
        </nav>

        <div className="crm-sidebar-foot">
          <div className="crm-user">
            <Avatar name={user} size={34} />
            <div>
              <strong>{user}</strong>
              <span>Zalogowano</span>
            </div>
          </div>
          <button type="button" className="crm-icon-btn" onClick={handleLogout} title="Wyloguj">
            <LogOut size={18} aria-hidden="true" />
          </button>
        </div>
      </aside>

      <main className="crm-main">
        <header className="crm-topbar">
          <div className="crm-topbar-title">
            <h1>{meta.title}</h1>
            <span>
              {loadState === "ready" && view !== "chats" && view !== "analytics"
                ? `${leads.length} zgłoszeń · ${meta.subtitle}`
                : meta.subtitle}
            </span>
          </div>
          <div className="crm-topbar-actions">
            <button type="button" className="crm-icon-btn" onClick={() => void load()} title="Odśwież" disabled={loadState === "loading"}>
              <RefreshCw size={18} aria-hidden="true" className={loadState === "loading" ? "crm-spin" : undefined} />
            </button>
            <button type="button" className="crm-icon-btn" onClick={toggleTheme} title="Zmień motyw">
              {theme === "dark" ? <Sun size={18} aria-hidden="true" /> : <Moon size={18} aria-hidden="true" />}
            </button>
          </div>
        </header>

        <div className="crm-content">
          {loadState === "loading" && <div className="crm-state">Ładowanie zgłoszeń…</div>}
          {loadState === "error" && (
            <div className="crm-state crm-state--error">
              <p>{errorMsg}</p>
              <button type="button" className="crm-btn crm-btn--primary" onClick={() => void load()}>
                Spróbuj ponownie
              </button>
            </div>
          )}
          {loadState === "ready" && view === "dashboard" && (
            <Dashboard
              leads={leads}
              tasks={tasks}
              onOpen={setSelectedId}
              onGoLeads={() => setView("leads")}
              onGoTasks={() => setView("tasks")}
              onToggleTask={toggleTask}
            />
          )}
          {loadState === "ready" && view === "pipeline" && (
            <Pipeline leads={leads} onOpen={setSelectedId} onMove={(id, status) => void updateLead(id, { status })} />
          )}
          {loadState === "ready" && view === "leads" && (
            <LeadsTable leads={leads} query={query} setQuery={setQuery} onOpen={setSelectedId} />
          )}
          {loadState === "ready" && view === "tasks" && (
            <TasksView
              tasks={tasks}
              leads={leads}
              onCreate={createTask}
              onToggle={toggleTask}
              onReschedule={rescheduleTask}
              onDelete={removeTask}
              onOpenLead={setSelectedId}
            />
          )}
          {loadState === "ready" && view === "analytics" && (
            <AnalyticsView days={analyticsDays} onDaysChange={setAnalyticsDays} onAuthError={onLogout} />
          )}
          {loadState === "ready" && view === "chats" && <ChatsView onAuthError={onLogout} />}
        </div>
      </main>

      {selected && (
        <LeadDrawer
          lead={selected}
          leads={leads}
          tasks={tasks}
          onClose={() => setSelectedId(null)}
          onUpdate={updateLead}
          onDeleteLead={removeLead}
          onCreateTask={createTask}
          onToggleTask={toggleTask}
          onRescheduleTask={rescheduleTask}
          onDeleteTask={removeTask}
        />
      )}
    </div>
  );
}
